import { useParams, Link } from "react-router-dom";
import { Stack } from "@altimateai/lego";
import { Typography, Button, Card, CardContent, CardHeader } from "@altimateai/lego";
import { format } from "date-fns";
import { BackButton } from "@/components/button";
import { RelativeComponentLoader } from "@/components/loader";
import { ColorInsightTags } from "@/components/tags";
import { useUsers } from "@/helpers/hooks";
import {
  IconCalendar,
  IconChartPie,
  IconEye,
  IconHash,
  IconInfoSquareRounded,
  IconUser,
} from "@tabler/icons-react";
import DeleteMemoryButton from "./DeleteMemoryButton";
import EditMemory from "./EditMemory";
import useMemories from "./useMemories";
import { useBreadcrumb } from "@/components/breadcrumbs";

const MCPMemoryDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { getByUuid } = useUsers();
  const { datamates, isFetching, memories, error } = useMemories({});

  const memory = memories.find(m => m.id === id);

  // Set breadcrumbs with memory title when available
  useBreadcrumb(memory?.metadata?.title ?? memory?.memory?.slice(0, 30) ?? "Loading...", {
    defaultParents: [
      { label: "Datamates", pathname: "/datamates" },
      { label: "Memory Hub", pathname: "/datamates/memories" },
    ],
  });
  const datamate = datamates?.find(d => d.id === memory?.metadata?.datamate_id);

  if (isFetching) {
    return <RelativeComponentLoader label="Loading memory details..." />;
  }

  if (error || !memory) {
    return (
      <Card>
        <CardHeader>
          <Stack>
            <Typography variant="h4">Memory not found</Typography>
          </Stack>
        </CardHeader>
        <CardContent>
          <Typography variant="body" className="al-text-gray-500 al-mb-4">
            The requested memory could not be found.
          </Typography>
          <Button variant="secondary" asChild>
            <Link to="/datamates/memories">View all memories</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const createdBy = getByUuid(memory.user_id);
  return (
    <Stack gap="4xl" direction="column">
      <Stack align="center" justify="between">
        <BackButton defaultUrl="/datamates/memories" />
        <Typography variant="page-heading" className="al-flex-1">
          {memory.metadata?.title || memory.memory}
        </Typography>
        <Stack>
          <EditMemory memory={memory} datamate={datamate} showButtonText />
          <DeleteMemoryButton memory={memory} />
        </Stack>
      </Stack>
      <Card>
        <CardHeader>
          <Stack gap="lg" align="center">
            <div className="al-rounded-md al-bg-blue-100 al-text-blue-500 al-p-2">
              <IconInfoSquareRounded />
            </div>
            <Typography variant="body">Metadata</Typography>
          </Stack>
        </CardHeader>
        <CardContent>
          <Stack direction="column" gap="2xl">
            {/* Details */}
            <Stack direction="column" gap="md" className="al-mt-4">
              <Stack direction="row" gap="8xl" wrap>
                <Stack align="start" direction="column">
                  <Stack gap="md" align="center">
                    <IconEye className="al-h-4 al-w-4 al-text-gray-500" />
                    <Typography variant="body" size="xs" className="al-text-gray-500">
                      Visibility:
                    </Typography>
                  </Stack>
                  <Typography variant="body" className="al-capitalize">
                    {datamate?.privacy || "private"}
                  </Typography>
                </Stack>
                <Stack align="start" direction="column">
                  <Stack gap="md" align="center">
                    <IconUser className="al-h-4 al-w-4 al-text-gray-500" />
                    <Typography variant="body" size="xs" className="al-text-gray-500">
                      Created By:
                    </Typography>
                  </Stack>
                  <Typography variant="body">
                    {createdBy?.first_name} {createdBy?.last_name}
                  </Typography>
                </Stack>
                <Stack align="start" direction="column">
                  <Stack gap="md" align="center">
                    <IconEye className="al-h-4 al-w-4 al-text-gray-500" />
                    <Typography variant="body" size="xs" className="al-text-gray-500">
                      Retrievals:
                    </Typography>
                  </Stack>
                  <Typography variant="body">12</Typography>
                </Stack>
                <Stack align="start" direction="column">
                  <Stack gap="md" align="center">
                    <IconCalendar className="al-h-4 al-w-4 al-text-gray-500" />
                    <Typography variant="body" size="xs" className="al-text-gray-500">
                      Last modified:
                    </Typography>
                  </Stack>
                  <Typography variant="body">
                    {format(new Date(memory.updated_at), "MMM dd, yyyy HH:mm")}
                  </Typography>
                </Stack>
                {memory.metadata?.datamate_id && (
                  <Stack align="start" direction="column">
                    <Stack gap="md" align="center">
                      <IconChartPie className="al-h-4 al-w-4 al-text-gray-500" />
                      <Typography variant="body" size="xs" className="al-text-gray-500">
                        Datamate:
                      </Typography>
                    </Stack>
                    <Typography variant="body" className="al-text-blue-500 hover:al-underline">
                      <Link to={`/datamate/${memory.metadata?.datamate_id}`}>
                        {memory.metadata?.datamate_name}
                      </Link>
                    </Typography>
                  </Stack>
                )}
                <Stack align="start" direction="column">
                  <Stack gap="md" align="center">
                    <IconCalendar className="al-h-4 al-w-4 al-text-gray-500" />
                    <Typography variant="body" size="xs" className="al-text-gray-500">
                      Created At:
                    </Typography>
                  </Stack>
                  <Typography variant="body">
                    {format(new Date(memory.created_at), "MMM dd, yyyy HH:mm")}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
            {/* Tags */}
            <Stack>
              {memory.categories && memory.categories.length > 0 && (
                <ColorInsightTags tags={memory.categories} onClick={() => {}} />
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <Stack gap="lg" align="center">
            <div className="al-rounded-md al-bg-green-100 al-text-green-500 al-p-2">
              <IconHash />
            </div>
            <Typography variant="body">Memory details</Typography>
          </Stack>
        </CardHeader>
        <CardContent>
          <Typography variant="body" className="al-text-gray-500 al-mb-4">
            {memory.memory}
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default MCPMemoryDetails;
